<template>
  <div
    ref="lfTableContainer"
    class="hover-row relative z-1"
    :class="{
      'h-full': !isHeightProvided && fullHeight,
      'freeze-first freeze-border-first':
        pinOption &&
        pinEnabled &&
        !showMultiselectControls &&
        !freezeFirstTwoColumnsWhenPinned,
      'freeze-first-two freeze-border-first-two':
        pinOption &&
        pinEnabled &&
        (showMultiselectControls || freezeFirstTwoColumnsWhenPinned),
      'freeze-last': pinOption && pinLastEnabled && hasLastColumnPinnable,
    }"
  >
    <div
      :id="customId || ID"
      class="custom-table-scrollbar overflow-y-overlay rounded-t shadow-main"
      data-cy="custom-table-scrollbar"
      :class="[
        { 'fixed-height': isHeightProvided },
        hideScrollbarX
          ? 'overflow-x-hidden hover:overflow-x-overlay'
          : 'overflow-x-overlay',
      ]"
    >
      <table
        v-bind="virtual ? containerProps : {}"
        class="w-full table-auto rounded-t bg-white px-1"
        :class="[noLeftRightBorder, { border: hasBorder }]"
        :data-cy="attrs?.['data-cy'] || 'lf-table'"
      >
        <component
          :is="tableVersion === 2 ? TableHeadV2 : TableHead"
          :class="[
            'table-head',
            noLeftRightBorder,
            { 'sticky top-0 z-50 bg-white': headerSticky },
          ]"
          :select-all="selectAll"
          :select-one="selectOne"
          :show-head-select-all="showHeadSelectAll"
          :has-select-mode-option="hasSelectModeOption"
          :is-select-mode-enabled="isSelectModeEnabled"
          :can-disable-select-mode="canDisableSelectMode"
          :columns="columns"
          :scrollable-element-id="scrollableElementId"
          :select-title="selectTitle"
          :header-no-wrap="headerNoWrap"
          :is-loading="isTableLoading"
          :is-selected-all-rows="allRowsSelected"
          :head-classes="headClasses"
          :pin-enabled="pinEnabled"
          :pin-last-enabled="pinLastEnabled"
          :pin-option="pinOption"
          :show-last-column-pin="showLastColumnPin"
          :disabled-checkboxes="disabledCheckboxes"
          @toggle-all-checkboxes="handleToggleAllCheckboxes"
          @toggle-sub-column="toggleSubColumns"
          @click:pin="handlePinClick"
          @click:pin-last="handleLastColumnPinClick"
          @multiselect:change="handleMultiselectChange"
        >
          <slot name="title" />
        </component>
        <tbody
          v-bind="virtual ? wrapperProps : {}"
          class="text-left text-headline"
        >
          <slot name="controls" />
          <tr v-if="tableEmpty">
            <td :colspan="columns.length" class="py-4 text-center">
              <slot name="empty">
                {{ noDataText }}
              </slot>
            </td>
          </tr>
          <template v-else>
            <template v-if="virtual">
              <table-row
                v-for="{ data: row, index } in virtualList"
                :key="row.id || index"
                :row="row"
                :row-index="index"
                :select-all="selectAll"
                :select-one="selectOne"
                :select-one-key="selectOneKey"
                :row-is-checked="selectedRowsSet.has(row.id)"
                :select-one-checked="
                  `${row.id}_${row[selectOneKey]}` === selectedRows?.[0]
                "
                :columns="columns"
                :accordion-table="accordionTable"
                :row-is-clickable="rowIsClickable"
                :row-is-inactive="rowIsInactive"
                :row-click-condition="rowClickCondition"
                :select-details-provider="selectDetailsProvider ?? undefined"
                :has-border="rowWithBorder"
                :disabled-checkboxes="disabledCheckboxes"
                :lazy-load="lazyLoad"
                :get-td-classes="getTdClasses"
                :get-tr-classes="getTrClasses"
                @row-clicked="rowClicked"
                @row-changed="rowChanged"
                @radio-selected="radioSelected"
              />
            </template>
            <template v-else>
              <table-row
                v-for="(row, index) in data"
                :key="row.id || index"
                :row="row"
                :row-index="index"
                :select-all="selectAll"
                :select-one="selectOne"
                :select-one-key="selectOneKey"
                :row-is-checked="selectedRowsSet.has(row.id)"
                :select-one-checked="
                  `${row.id}_${row[selectOneKey]}` === selectedRows?.[0]
                "
                :columns="columns"
                :accordion-table="accordionTable"
                :row-is-clickable="rowIsClickable"
                :row-is-inactive="rowIsInactive"
                :row-click-condition="rowClickCondition"
                :select-details-provider="selectDetailsProvider ?? undefined"
                :has-border="rowWithBorder"
                :disabled-checkboxes="disabledCheckboxes"
                :lazy-load="lazyLoad"
                :get-td-classes="getTdClasses"
                :get-tr-classes="getTrClasses"
                @row-clicked="rowClicked"
                @row-changed="rowChanged"
                @radio-selected="radioSelected"
              />
            </template>
          </template>
        </tbody>
      </table>
    </div>
    <lf-pagination
      v-if="metadata"
      :style="paginationStyle"
      :show-per-page="showPerPage"
      :metadata="metadata"
      :is-absolute="paginationAbsolute"
      @change-page="changePage"
      @change-items-per-page="changeItemsPerPage"
    />
    <!-- adding blur to the right edge to indicate scolling -->
    <div
      v-if="hideScrollbarX"
      class="absolute top-0 right-0 h-full w-8 bg-linear-to-r from-transparent to-white/10"
    />
    <div v-if="isTableLoading" class="absolute inset-0">
      <div class="h-full w-full bg-white">
        <loader
          class="table-loader"
          :is-loading="isTableLoading"
          :z-index="40"
        />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { useElementSize, useVirtualList } from "@vueuse/core";
import {
  computed,
  inject,
  onMounted,
  ref,
  toRef,
  useAttrs,
  useTemplateRef,
  watch,
} from "vue";

import { HEIGHT_REGEX_PATTERN } from "@/helpers/UI";
import { CLEAR_LF_TABLE_ROWS } from "@/helpers/constants/events";
import { useEmitter } from "@/hooks/common";

import Loader from "@/components/ui/Loader.vue";
import LfPagination from "@/components/ui/table/LfPagination.vue";
import TableHead from "@/components/ui/table/TableHead.vue";
import TableHeadV2 from "@/components/ui/table/TableHeadV2.vue";
import TableRow from "@/components/ui/table/TableRow.vue";

import type { IDealsColumn, TableColumnV2 } from "@/models/applications";
import type {
  IResponseMeta,
  LazyLoadOptions,
  SelectDetailsProvider,
} from "@/models/common";
import type { Ref } from "vue";

const {
  tableVersion = 1 as 1 | 2,
  columns,
  data = null,
  metadata = null,
  selectAll = false,
  selectOne = false,
  selectOneKey = "",
  showHeadSelectAll = true,
  rowIsClickable = false,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rowIsInactive = (() => false) as (...args: any[]) => boolean,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rowClickCondition = (() => true) as (...args: any[]) => unknown,
  selectDetailsProvider = null,
  selectTitle = "",
  showPerPage = true,
  noDataText = "No Data Available",
  hideScrollbarX = false,
  headerNoWrap = true,
  isTableLoading = false,
  customId,
  checkedRows = [] as (string | number)[],
  scrollableElementId = "",
  hasBorder = true,
  rowWithBorder = true,
  headClasses = "border",
  accordionTable = false,
  paginationAbsolute = true,
  pinOption = false,
  pinEnabled = false,
  pinLastEnabled = false,
  freezeFirstTwoColumnsWhenPinned = false,
  fullHeight = true,
  /** Container must have fixed height for sticky header to work */
  headerSticky = false,
  disabledCheckboxes = false,
  lazyLoad = true as boolean | LazyLoadOptions,
  hasSelectModeOption = false,
  isSelectModeEnabled = false,
  canDisableSelectMode = true,
  showMultiselectControls = false,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getTdClasses = (() => "") as (...args: any[]) => string | undefined,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getTrClasses = (() => "") as (...args: any[]) => string | undefined,
  virtual = false,
  showLastColumnPin = false,
} = defineProps<{
  tableVersion?: 1 | 2;
  columns: (IDealsColumn | TableColumnV2)[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any[] | null;
  metadata?: IResponseMeta | null;
  selectAll?: boolean;
  selectOne?: boolean;
  selectOneKey?: string;
  showHeadSelectAll?: boolean;
  rowIsClickable?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rowIsInactive?: (...args: any[]) => boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rowClickCondition?: (...args: any[]) => unknown;
  selectDetailsProvider?: SelectDetailsProvider | null;
  selectTitle?: string;
  showPerPage?: boolean;
  noDataText?: string;
  hideScrollbarX?: boolean;
  headerNoWrap?: boolean;
  isTableLoading?: boolean;
  customId?: string;
  checkedRows?: (string | number)[];
  scrollableElementId?: string;
  hasBorder?: boolean;
  rowWithBorder?: boolean;
  headClasses?: string;
  accordionTable?: boolean;
  paginationAbsolute?: boolean;
  pinOption?: boolean;
  pinEnabled?: boolean;
  pinLastEnabled?: boolean;
  freezeFirstTwoColumnsWhenPinned?: boolean;
  fullHeight?: boolean;
  headerSticky?: boolean;
  disabledCheckboxes?: boolean;
  lazyLoad?: boolean | LazyLoadOptions;
  hasSelectModeOption?: boolean;
  isSelectModeEnabled?: boolean;
  canDisableSelectMode?: boolean;
  showMultiselectControls?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getTdClasses?: (...args: any[]) => string | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getTrClasses?: (...args: any[]) => string | undefined;
  virtual?: boolean;
  showLastColumnPin?: boolean;
}>();

const SCROLLBAR_WIDTH = 8;
const TABLE_PAGINATION_HEIGHT_PX = 50;

const emitter = useEmitter();

const attrs = useAttrs();
const emit = defineEmits<{
  selectedRow: [id: string | number, checked: boolean];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectedRows: [rows: any[], isChecked?: boolean];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  "row-clicked": [value: any];
  changePage: [page: number];
  changeItemsPerPage: [itemsPerPage: number, maxPage: number | null];
  toggleSubColumn: [key: string];
  "update:pinEnabled": [value: boolean];
  "update:pinLastEnabled": [value: boolean];
  "multiselect:change": [value: boolean];
}>();

const ID = inject("ID", undefined);
const selectedRows = ref(checkedRows);
const lfTableContainer = useTemplateRef("lfTableContainer");
const { height: lfTableContainerHeight } = useElementSize(lfTableContainer);

const rowIds = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (data?.map((row: any) => row.id) || []) as (string | number)[];
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const reactiveData = toRef(() => data ?? []) as Ref<any[]>;
const {
  list: virtualList,
  containerProps,
  wrapperProps,
} = useVirtualList(virtual ? reactiveData : [], {
  itemHeight: 60,
  overscan: 5,
});

const tableEmpty = computed(() => !Object.keys(data ?? {}).length);

const selectedRowsSet = computed(() => new Set(selectedRows.value));

const tableHeight = computed(() => {
  let value = lfTableContainerHeight.value;
  if (!paginationAbsolute) {
    // Reducing by pagination height + the width of the scrollbar
    // to have a gap between the table and pagination component
    value = Math.floor(value - (TABLE_PAGINATION_HEIGHT_PX + SCROLLBAR_WIDTH));
  }
  return value;
});

const paginationStyle = computed(() => {
  const style = new Map();
  style.set("height", TABLE_PAGINATION_HEIGHT_PX + "px");
  return Object.fromEntries(style);
});

const isHeightProvided = computed(() => {
  const classes = (attrs.class as string) || "";
  return classes.match(HEIGHT_REGEX_PATTERN);
});

const allRowsSelected = computed(() => {
  return (
    !!rowIds.value.length &&
    !!selectedRows.value.length &&
    rowIds.value.every((id) => selectedRows.value.includes(id))
  );
});

const hasLastColumnPinnable = computed(() => {
  return showLastColumnPin && Boolean(columns?.length);
});

const handleToggleAllCheckboxes = (isChecked: boolean) => {
  if (isChecked) {
    selectedRows.value = Array.from(
      new Set([...selectedRows.value, ...rowIds.value])
    );
  } else {
    selectedRows.value = selectedRows.value.filter(
      (id) => !rowIds.value.includes(id)
    );
  }

  emit("selectedRows", selectedRows.value, isChecked);
};

const handleMultiselectChange = (isMultiselectEnabled: boolean) => {
  selectedRows.value = [];
  emit("multiselect:change", isMultiselectEnabled);
};

const rowChanged = (checked: boolean, id: string | number) => {
  const numId = Number(id);
  const changedValue = isNaN(numId) ? id : numId;
  if (checked) {
    selectedRows.value.push(changedValue);
  } else {
    selectedRows.value.splice(selectedRows.value.indexOf(changedValue), 1);
  }
  emit("selectedRow", id, checked);
  emit("selectedRows", selectedRows.value);
};

const radioSelected = (id: string | number) => {
  selectedRows.value = [id];
  emit("selectedRow", id, true);
};

const noLeftRightBorder = (attrs as { class: string })?.class?.includes(
  "freeze-border-last"
)
  ? "border-l-0 border-r-0"
  : "";

const rowClicked = (value: unknown) => {
  emit("row-clicked", value);
};

const changePage = (page: number) => {
  emit("changePage", page);
};

const changeItemsPerPage = (itemsPerPage: number, maxPage: number | null) => {
  emit("changeItemsPerPage", itemsPerPage, maxPage);
};

const toggleSubColumns = (key: string) => {
  emit("toggleSubColumn", key);
};

const handlePinClick = (value: boolean) => {
  emit("update:pinEnabled", value);
};

const handleLastColumnPinClick = (value: boolean) => {
  emit("update:pinLastEnabled", value);
};

watch(
  () => checkedRows,
  (val) => {
    selectedRows.value = val;
  }
);

onMounted(() => {
  emitter?.on(CLEAR_LF_TABLE_ROWS, () => {
    selectedRows.value = [];
  });
});

defineExpose({ selectedRows });
</script>

<style>
@reference "@/assets/tailwind.css";

.items-list table,
.items-list thead {
  @apply border-none text-gray-700;
}

.items-list table {
  @apply w-full table-auto border-separate border-spacing-y-4;
}

.items-list th {
  @apply bg-white;
}

.items-list td {
  @apply border-x-0 border-y py-2 first:rounded-l-md first:border-l last:rounded-r-md last:border-r;
}

.freeze-first table td:first-child,
.freeze-first table th:first-child {
  background: white;
  border-left: 1px solid transparent;
  position: sticky !important;
  left: 0;
  z-index: 2;
}

.freeze-first-two table td:nth-child(-n + 2),
.freeze-first-two table th:nth-child(-n + 2) {
  background: white;
  border-left: 1px solid transparent;
  position: sticky;
  z-index: 2;
}

.freeze-first-two table th:nth-child(-n + 2) {
  top: 0;
}

.freeze-first-two table td:first-child,
.freeze-first-two table th:first-child {
  left: 0;
}

.freeze-first-two table td:nth-child(2),
.freeze-first-two table th:nth-child(2) {
  left: 56px;
}

.freeze-first-two table td.bg-divider {
  @apply bg-grey-hover;
  top: 49px;
}

.freeze-first-two table tr:hover td:nth-child(-n + 2) {
  @apply bg-grey-hover;
}

.freeze-first-two table td:first-child:hover,
.freeze-first-two table td:nth-child(2):hover {
  @apply bg-grey-hover;
}

.freeze-first-two table tr.selected-row td {
  background: #edf4fe;
}

.freeze-first-two table tr.selected-row:hover td {
  background: #edf4fe;
}

.freeze-border-first-two table th:nth-child(2):after,
.freeze-border-first-two table td:nth-child(2):after {
  content: "";
  position: absolute;
  top: 0;
  right: -30px;
  height: 100%;
  width: 30px;
  box-shadow: 15px 0 30px -30px gray inset !important;
  pointer-events: none;
}

.freeze-border-first table th:first-child:before,
.freeze-border-first table td:first-child:before,
.freeze-border-last table th:last-child:before,
.freeze-border-last table td:last-child:before {
  position: absolute;
  content: "";
  left: -1px;
  right: -1px;
  top: 0;
  height: 100%;
  width: 100%;
  pointer-events: none;
}

.freeze-border-first table th:first-child:after,
.freeze-border-first table td:first-child:after {
  content: "";
  position: absolute;
  top: 0;
  right: -30px;
  height: 100%;
  width: 30px;
  box-shadow: 15px 0 30px -30px gray inset !important;
  pointer-events: none;
}

.freeze-border-last table th:last-child:after,
.freeze-border-last table td:last-child:after {
  content: "";
  position: absolute;
  top: 0;
  left: -30px;
  height: 100%;
  width: 30px;
  pointer-events: none;
  box-shadow: -15px 0 30px -30px rgba(83, 90, 97, 0.6) inset !important;
}

.table-head {
  box-shadow:
    0 1px rgb(228, 231, 235) inset,
    0 -1px rgb(228, 231, 235) inset;
}

.freeze-last table td:last-child,
.freeze-last table th:last-child {
  background: white;
  position: sticky !important;
  right: 0;
  z-index: 2;
}

.freeze-last table td:last-child {
  @apply shadow-fab;
}

.freeze-last table th:last-child,
.freeze-first table th:first-child {
  z-index: 3;
}

.freeze-header table th {
  background: white;
  position: sticky !important;
  top: 0;
  z-index: 2;
}

.freeze-border-header table th:before {
  position: absolute;
  content: "";
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  border-top-width: 1px;
  border-bottom-width: 1px;
  pointer-events: none;
}

.freeze-border-header table {
  border-top: none;
}

.freeze-border-header thead {
  border: none;
}

table tbody th {
  position: sticky;
  left: 0;
  background: white;
  z-index: 0;
}

.hover-row table tbody tr:hover {
  @apply bg-grey-hover;
}

.table-loader {
  max-height: 85vh;
}

.fixed-height {
  height: v-bind(tableHeight + "px");
}

@-moz-document url-prefix() {
  .fixed-height {
    overflow-y: auto;
    overflow-x: auto;
    scrollbar-gutter: stable;
  }
}

.custom-table-scrollbar::-webkit-scrollbar {
  background: transparent;
  height: v-bind(SCROLLBAR_WIDTH + "px");
  width: v-bind(SCROLLBAR_WIDTH + "px");
}

.custom-table-scrollbar::-webkit-scrollbar-corner {
  background: transparent;
}

.custom-table-scrollbar::-webkit-scrollbar-thumb {
  border-radius: 100px;
  background-color: rgba(133, 133, 133, 0.5);
  -webkit-box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
  box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
}
</style>
